import * as React from 'react';
import { ArrayMinOne } from '@estee/elc-universal-utils';
import { getRootMethods, IConfig, requestConfigs, shallowMerge, MergeMethod } from './utils';
import {
    Context,
    ContextValueType,
    IProps as UpdateContextProvideProps,
    UpdateContextProvider
} from './Context';
import { ContextMerger } from './ContextMerger';

type ConfigType = { config: IConfig };

interface IGetConfigProps extends UpdateContextProvideProps<ConfigType> {
    configFields: ArrayMinOne<string>;
    mergeConfigs: MergeMethod<IConfig, IConfig | object>;
    configFromProps?: IConfig;
}

function areObjectEqual(objA: object = {}, objB: object = {}) {
    return JSON.stringify(objA) === JSON.stringify(objB);
}

class GetConfig extends UpdateContextProvider<ConfigType, IGetConfigProps> {
    static displayName = 'GetConfig';

    constructor(props: IGetConfigProps) {
        super(props);

        this.setConfig();
    }

    private setConfig() {
        const { configFields, configFromProps, mergeConfigs } = this.props;

        const globalConfig = getRootMethods().getConfigs(configFields);

        if (globalConfig) {
            this.hasValue = true;
            const config = mergeConfigs(globalConfig, configFromProps || {});

            this.updateContextProvider(config && { config });
        }
    }

    public componentDidUpdate(prevProps: IGetConfigProps) {
        const { configFromProps } = this.props;
        if (!areObjectEqual(configFromProps, prevProps.configFromProps)) {
            this.setConfig();
        }
    }

    public async componentDidMount() {
        const { configFields, configFromProps, mergeConfigs } = this.props;

        if (!this.hasValue) {
            const configResponse = await requestConfigs(configFields);

            if (configResponse) {
                const config = mergeConfigs(configResponse, configFromProps || {});
                getRootMethods().addConfigs(configResponse);

                this.updateContextProvider({ config });
            }
        }
    }
}

interface IOnlyLocalConfigType extends UpdateContextProvideProps<ConfigType> {
    configFromProps?: IConfig;
}

class OnlyLocalConfig extends UpdateContextProvider<ConfigType, IOnlyLocalConfigType> {
    constructor(props: IOnlyLocalConfigType) {
        super(props);
        this.hasValue = true;
        this.updateContextProvider({ config: props.configFromProps });
    }

    public componentDidUpdate(prevProps: IGetConfigProps) {
        const { configFromProps } = this.props;

        if (!areObjectEqual(configFromProps, prevProps.configFromProps)) {
            this.updateContextProvider(configFromProps && { config: configFromProps });
        }
    }
}

type IAddConfigProps = { config?: IConfig };
export function addConfig(
    configFields: ArrayMinOne<string> | null,
    mergeConfigs = shallowMerge as MergeMethod<IConfig, IConfig | object>
) {
    getRootMethods().registerConfigFields(configFields || []);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (WrappedComponent: any): any => (props: IAddConfigProps) => (
        <Context.Consumer>
            {({ updateProvider, config: configFromContext }: ContextValueType<ConfigType>) => {
                if (configFields) {
                    return (
                        <GetConfig
                            configFields={configFields}
                            configFromProps={props.config}
                            mergeConfigs={mergeConfigs}
                            updateContextProvider={updateProvider}
                            contextHasValue={Boolean(configFromContext)}
                        >
                            <WrappedComponent {...props} />
                        </GetConfig>
                    );
                }

                return (
                    <OnlyLocalConfig
                        configFromProps={props.config}
                        updateContextProvider={updateProvider}
                        contextHasValue={Boolean(configFromContext)}
                    >
                        <WrappedComponent {...props} />
                    </OnlyLocalConfig>
                );
            }}
        </Context.Consumer>
    );
}

export function configConsumer(configFields: ArrayMinOne<string>, mergeConfigs = shallowMerge) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (WrappedComponent: any): any =>
        class ConfigConsumer extends React.Component<{ config?: IConfig }> {
            static displayName = 'configConsumer';

            mergeConfig = (contextConfig: IConfig) => {
                return mergeConfigs(contextConfig, this.props.config || {});
            };

            public render() {
                return (
                    <Context.Consumer>
                        {({ config: configFromContext }: ContextValueType<{ config: IConfig }>) => (
                            <ContextMerger
                                object={configFromContext || {}}
                                keys={configFields}
                                mergeObject={this.mergeConfig}
                                render={(requestedConfig: IConfig) => (
                                    <WrappedComponent {...this.props} config={requestedConfig} />
                                )}
                            />
                        )}
                    </Context.Consumer>
                );
            }
        };
}
